import { IssueCreationSource, useCustomQuery } from '../../graphql';

/**
 * The integration creation sources whose readings surface as asset-scoped
 * activity rows. String literals (not `IssueCreationSource.*`) so the query
 * variable stays correct even when the enum module is mocked in tests — the
 * enum value IS the string.
 */
export const INTEGRATION_READING_SOURCES: readonly string[] = [
  'GEOTAB',
  'JOHN_DEERE',
];

/**
 * One integration-sourced counter reading, read straight from the
 * `assetroutine` collection. Every stored integration reading is a *changed*
 * value by construction (the sync worker writes only on change, and the
 * `(asset, routine, sourceTimestamp)` unique index dedupes), so "show only when
 * changed" falls out for free — there is nothing to filter at read time beyond
 * the source / draft / revision guards.
 */
export interface AssetIntegrationReading {
  id: string;
  createdAt?: string;
  createdFrom?: IssueCreationSource;
  routine?: { id: string };
  /** Counter payload: { count, oldValue?, userCount?, uom? }. */
  data?: {
    count?: number;
    oldValue?: number;
    userCount?: number;
    /** Unit of measure carried on the reading itself (e.g. "mi", "hours"). */
    uom?: string;
  } | null;
}

/**
 * Loads integration-sourced (GeoTab / John Deere) counter readings for a single
 * asset, newest first, for read-time rendering as lightweight activity rows on
 * the asset detail surfaces. No records are written — these are the existing
 * `assetroutine` docs the sync worker already created.
 *
 * Exclusions baked into the filter:
 * - only `createdFrom in (GEOTAB, JOHN_DEERE)`
 * - `isDraft: { ne: true }` (drafts never apply to integration readings, but the
 *   guard is cheap and future-proofs against a manual-draft sharing the routine)
 *
 * Revision-superseded docs: integration readings are never revised (verified:
 * the public-API readings path never sets `revisionOf`; only edited manual
 * submissions build `revisionOf` chains). A `revisionOf: { eq: null }` filter is
 * NOT used — Graphback's Mongo query builder runs every relationship-id filter
 * value through `uuid-mongodb`'s `from()`, which throws
 * `Cannot read properties of null (reading '_bsontype')` on a null id (the
 * documented UUID-binData gotcha). Since integration readings have no revision
 * chains, there is nothing to exclude.
 *
 * Soft-deleted docs are filtered by Graphback automatically.
 */
export const useAssetIntegrationReadings = (
  assetId: string | undefined,
  disabled?: boolean
) => {
  const { data, isLoading } = useCustomQuery({
    query: FIND_ASSET_INTEGRATION_READINGS,
    cacheKey: ['assetIntegrationReadings', assetId],
    variables: {
      filter: {
        asset: { eq: assetId },
        createdFrom: { in: INTEGRATION_READING_SOURCES },
        isDraft: { ne: true },
      },
      orderBy: { field: 'createdAt', order: 'DESC' },
    },
    options: {
      enabled: !!assetId && !disabled,
    },
  });

  const readings: AssetIntegrationReading[] =
    data?.findAssetRoutines?.items?.filter(Boolean) ?? [];

  return { readings, isLoading };
};

const FIND_ASSET_INTEGRATION_READINGS = `
  query FindAssetIntegrationReadings(
    $filter: AssetRoutineFilter
    $orderBy: OrderByInput
  ) {
    findAssetRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        id
        createdAt
        createdFrom
        routine {
          id
        }
        data
      }
    }
  }
`;
